import React, { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { ScanLine, CheckCircle2, AlertTriangle, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const isHbsCode = (s) => /^HBS\d{12}$/.test((s || "").trim().toUpperCase());

export default function Scan() {
  const { user, logout, can } = useAuth();
  const [manual, setManual] = useState("");
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState("");
  const [location, setLocation] = useState("Warehouse");
  const [deviceId] = useState(() => `${navigator.userAgent.includes("Android") ? "TC56" : "WEB"}-${Math.floor(Math.random() * 9000 + 1000)}`);
  const bufferRef = useRef("");
  const lastKeyTimeRef = useRef(0);

  const submitScan = useCallback(async (code) => {
    const clean = code.trim().toUpperCase();
    if (!isHbsCode(clean)) {
      setLastError(`Not a valid HBS tracking number: "${clean}"`);
      toast.error("Invalid HBS code");
      return;
    }
    setBusy(true);
    setLastError("");
    try {
      const { data } = await api.post("/scan", { tracking_id: clean, location, device_id: deviceId });
      setRecent((r) => [{
        ok: true,
        tracking_id: data.tracking_id,
        prev: data.previous_status,
        next: data.new_status,
        ts: new Date().toLocaleTimeString(),
      }, ...r].slice(0, 20));
      toast.success(`${data.tracking_id}: ${data.previous_status} → ${data.new_status}`);
      if (data.new_status === "Delivered") {
        // Confirmation prompt
        setTimeout(() => {
          toast.success("✓ Delivered. Confirmation recorded.");
        }, 200);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Scan failed";
      setLastError(msg);
      setRecent((r) => [{ ok: false, tracking_id: clean, error: msg, ts: new Date().toLocaleTimeString() }, ...r].slice(0, 20));
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }, [location, deviceId]);

  // Zebra TC56 DataWedge / USB keyboard wedge support
  // Supports THREE input channels:
  //   1) Native DataWedge intent broadcast — if the Android wrapper bridges intents to
  //      window.HBSScanner.onBarcode(...) or dispatches a 'datawedge:scan' CustomEvent
  //   2) postMessage from a parent native shell (window.postMessage({type:'scan', code})).
  //   3) Keyboard-wedge fallback — listens for fast keystrokes ending in Enter.
  useEffect(() => {
    // Expose a global hook for the Android wrapper / WebView bridge
    window.HBSScanner = {
      onBarcode: (code) => {
        if (code) submitScan(String(code));
      },
    };
    const onDataWedge = (e) => {
      const code = e?.detail?.barcode || e?.detail?.code;
      if (code) submitScan(String(code));
    };
    const onPostMsg = (e) => {
      const msg = e?.data;
      if (msg && (msg.type === "scan" || msg.type === "datawedge")) {
        const code = msg.code || msg.barcode;
        if (code) submitScan(String(code));
      }
    };
    window.addEventListener("datawedge:scan", onDataWedge);
    window.addEventListener("message", onPostMsg);

    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const inField = ["input", "textarea"].includes(tag) && e.target.dataset.scanner !== "off";
      if (inField) return;
      const now = Date.now();
      const dt = now - lastKeyTimeRef.current;
      lastKeyTimeRef.current = now;
      if (dt > 80) bufferRef.current = "";
      if (e.key === "Enter") {
        const code = bufferRef.current;
        bufferRef.current = "";
        if (code.length >= 6) submitScan(code);
      } else if (e.key.length === 1) {
        bufferRef.current += e.key;
      }
    };
    window.addEventListener("keypress", onKey);
    return () => {
      window.removeEventListener("keypress", onKey);
      window.removeEventListener("datawedge:scan", onDataWedge);
      window.removeEventListener("message", onPostMsg);
      try { delete window.HBSScanner; } catch { /* noop */ }
    };
  }, [submitScan]);

  const onManualSubmit = (e) => {
    e.preventDefault();
    if (manual) {
      submitScan(manual);
      setManual("");
    }
  };

  return (
    <div data-testid="scan-page" className="min-h-screen" style={{ background: "#0F1D14", color: "#F5EFE0" }}>
      <header className="px-5 py-4 border-b border-[#D4AF37]/30 flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">HBS Scanner</div>
          <div className="text-sm font-mono">{deviceId}</div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/admin" data-testid="link-admin" className="text-xs text-[#D4AF37] underline">Admin</Link>
          <button onClick={logout} data-testid="logout-btn" className="text-[#F5EFE0]/70 hover:text-[#C8102E] p-2">
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-5">
        <div className="bg-[#1B3022] border border-[#D4AF37]/40 rounded-sm p-5">
          <div className="flex items-center gap-3 mb-4">
            <ScanLine className="text-[#D4AF37]" />
            <div>
              <div className="text-sm font-medium">{user?.email}</div>
              <div className="text-[10px] tracking-widest uppercase text-[#D4AF37]/70">{user?.role}</div>
            </div>
          </div>

          <label className="block text-[10px] tracking-[0.25em] uppercase text-[#F5EFE0]/70 mb-1">Scan Location</label>
          <select
            data-testid="scan-location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="w-full px-3 py-2 bg-[#0F1D14] border border-[#D4AF37]/30 rounded-sm text-[#F5EFE0] mb-4 text-sm"
          >
            {["Warehouse", "Loading Dock", "Vehicle", "PAP Hub", "Delivery Point"].map((l) => <option key={l}>{l}</option>)}
          </select>

          <p className="text-center text-xs text-[#D4AF37]/70 tracking-widest mb-3 uppercase">
            ❖ Point Zebra TC56 at barcode — DataWedge + keyboard wedge listening ❖
          </p>

          <form onSubmit={onManualSubmit} className="flex gap-2">
            <input
              data-testid="scan-manual-input"
              data-scanner="off"
              value={manual}
              onChange={(e) => setManual(e.target.value.toUpperCase())}
              placeholder="HBS000000000001"
              className="flex-1 px-4 py-3 bg-[#0F1D14] border border-[#D4AF37]/30 rounded-sm text-[#F5EFE0] outline-none font-mono tracking-widest"
              autoFocus
            />
            <button type="submit" disabled={busy} data-testid="scan-submit" className="btn-gold rounded-sm px-5 py-3 text-xs">
              {busy ? <Loader2 className="animate-spin" size={14} /> : "Scan"}
            </button>
          </form>
          {lastError && (
            <p data-testid="scan-error" className="mt-3 text-sm text-[#E5A07A] flex items-center gap-2">
              <AlertTriangle size={14} /> {lastError}
            </p>
          )}
          <div className="mt-3 text-[10px] tracking-widest uppercase text-[#F5EFE0]/40">
            Permissions: receive {can("can_receive_package") ? "✓" : "✗"} • transit {can("can_update_transit_status") ? "✓" : "✗"} • deliver {can("can_mark_as_delivered") ? "✓" : "✗"}
          </div>
        </div>

        <h2 className="font-serif-bv text-lg text-[#D4AF37] mt-8 mb-3">Recent Scans</h2>
        <ul data-testid="recent-scans" className="space-y-2">
          {recent.length === 0 && <li className="text-sm text-[#F5EFE0]/40">No scans yet — fire away.</li>}
          {recent.map((r, i) => (
            <li
              key={i}
              data-testid={`scan-row-${i}`}
              className={`p-3 border rounded-sm flex items-center gap-3 ${r.ok ? "border-[#3FA859]/40 bg-[#1F3D26]/50" : "border-[#C8102E]/50 bg-[#3D241B]/50"}`}
            >
              {r.ok ? <CheckCircle2 className="text-[#90D4A0]" size={18} /> : <AlertTriangle className="text-[#E5A07A]" size={18} />}
              <div className="flex-1 min-w-0">
                <div className="font-mono text-sm tracking-widest">{r.tracking_id}</div>
                <div className="text-[11px] text-[#F5EFE0]/70">
                  {r.ok ? `${r.prev} → ${r.next}` : r.error}
                </div>
              </div>
              <span className="text-[10px] text-[#F5EFE0]/40">{r.ts}</span>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}
